import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from 'environments/environment';
import { PlanDTO } from '@app/khc/_models/plan-dto';
import { UpdateFactDTO } from '@app/khc/_models/update-fact-dto';
import { PaymentDTO } from '@app/khc/_models/payment-dto';
import { AddConstructiveDTO } from '@app/khc/_models/add-constructive-dto';
import { ConstructiveDTO } from '@app/khc/_models/constructive-dto';
import { IdDTO } from '@app/khc/_models/id-dto';
import { LagDTO } from '../khc/_models/lag-dto';
import { DevelopmentDTO } from '@app/khc/_models/development-dto';
import { ShareholderDTO } from '@app/khc/_models/shareholders-dto';
import { AddShareholderDTO } from '@app/khc/_models/add-shareholder-dto';
import { Table7DataDTO } from '@app/khc/_models/table7-data-dto';
import { Table8DataDTO } from '@app/khc/_models/table8-data-dto';
import { VerticalApplicationDTO } from '@app/khc/_models/vertical-application-dto';
import { FinancingPlanDTO } from '../khc/_models/financing-plan-dto';
import { Table9DataDTO } from '../khc/_models/table9-data-dto';
import { IncomeDTO } from '@app/khc/_models/income-dto';
import { ShareholderPaymentDTO } from '@app/khc/_models/shareholder-payment-dto';
import { Table6BeforeDataDTO } from '@app/khc/_models/table6-before-data-dto';
import { Table6DataDTO } from '@app/khc/_models/table6-data-dto';
import { Table3AdditionalDataDTO } from '@app/khc/_models/table3-additional-data-dto';
import { Table7AdditionalDataDTO } from '@app/khc/_models/table7-additional-data-dto';
import { PaymentDataDTO } from '@app/khc/_models/payment-data-dto';
import { AddCommentDTO } from '@app/_models/add-comment-dto';
import { CommentDTO } from '@app/_models/comment-dto';
import { Table3DataDTO } from '@app/khc/_models/table3-data-dto';
import { Table3Data } from '@app/khc/_models/table3-data';
import { ObjectDTO } from '@app/khc/_models/object-dto';
import { PrescriptionDTO } from '../khc/_models/prescription-dto';
import { UpdatePrescriptionDataDTO } from '@app/khc/_models/update-prescription-data-dto';
import { CuratorDTO } from '@app/khc/_models/curator-dto';
import { NewProlongationDTO } from '@app/khc/_models/new-prolongation-dto';
import { ProlongationDTO } from '@app/khc/_models/prolongation-dto';
import { PrescriptionCategoryDTO } from '../khc/_models/prescription-category-dto';

@Injectable({
  providedIn: 'root'
})
export class KhcService {
  private http = inject(HttpClient);


  startProcess(reportId: string) : Observable<any> {
    return this.http.post(`${environment.apiUrl}/api/khc/start-monthly-report-process/${reportId}`, {});
  }

  getMonthlyReportById(reportId: string) : Observable<any> {
    return this.http.get(`${environment.khcUrl}/monthly-reports/${reportId}`);
  }

  getMonthlyReportXmlById(reportId: string) : Observable<string> {
    return this.http.get<string>(`${environment.khcUrl}/monthly-reports/${reportId}/xml`);
  }

  putSignature(reportId: string, signature: string) {
    return this.http.post(`${environment.khcUrl}/monthly-reports/${reportId}/signatures`, {signature: signature});
  }

  putReviewComment(reportId: string, commentData: { comment: any; }) {
    return this.http.post(`${environment.khcUrl}/monthly-reports/${reportId}/comments`, commentData);
  }

  getCommentById(reportId: string): Observable<any> {
    return this.http.get(`${environment.khcUrl}/monthly-reports/${reportId}/comments`);
  }

  createObject(createObjectDto: any) {
    return this.http.post(`${environment.khcUrl}/objects`, createObjectDto);
  }

  getMonthlyReportsByObjectId(objectId: string) : Observable<any[]> {
    return this.http.get<any[]>(`${environment.khcUrl}/objects/${objectId}/monthly-reports`);
  }

  addProgressEntry(reportId: string, entry: any) : Observable<any> {
    return this.http.post(`${environment.khcUrl}/monthly-reports/${reportId}/progress-entry`, entry);
  }

  saveProgressEntries(reportId: string, progressEntries: any[]) {
    return this.http.post(`${environment.khcUrl}/monthly-reports/${reportId}/progress-entries`, progressEntries);
  }

  getObjectPlansForMonths(objectId?: string, month?: string): Observable<PlanDTO[]> {
    return this.http.get<PlanDTO[]>(`${environment.khcUrl}/objects/${objectId}/constructive-plans?month=${month}`);
  }

  saveFacts(facts: UpdateFactDTO[]) {
    return this.http.put<PlanDTO[]>(`${environment.khcUrl}/facts`, facts);
  }

  // payments(objectId?: string): Observable<PaymentDTO[]> {
  //   return this.http.get<PaymentDTO[]>(`${environment.khcUrl}/objects/${objectId}/payments`);
  // }

  paymentsByReportId(reportId?: string): Observable<PaymentDTO[]> {
    return this.http.get<PaymentDTO[]>(`${environment.khcUrl}/payments?monthlyReportId=${reportId}`);
  }

  setPaymentKhcType(paymentId?: string, typeCode?: string, typeName?: string) {
    const data = {
      paymentId: paymentId,
      typeCode: typeCode,
      typeName: typeName,
    }
    return this.http.post<PaymentDTO>(`${environment.khcUrl}/payments`, data);
  }

  saveFinance(objectId?: string, data?: any) : Observable<any> {
    return this.http.post<any>(`${environment.khcUrl}/objects/${objectId}/finances`, data);
  }

  getObject(objectId?: string) {
    return this.http.get<any>(`${environment.khcUrl}/objects/${objectId}`);
  }

  addConstructive(objectId: string, dto: AddConstructiveDTO) : Observable<ConstructiveDTO> {
    return this.http.post<ConstructiveDTO>(`${environment.khcUrl}/objects/${objectId}/constructives`, dto);
  }

  deleteConstructive(constructiveId: string) : Observable<IdDTO> {
    return this.http.delete<IdDTO>(`${environment.khcUrl}/constructives/${constructiveId}`);
  }

  months(objectId?: string): Observable<string[]> {
    return this.http.get<string[]>(`${environment.khcUrl}/objects/${objectId}/months`);
  }

  lags(reportId?: string): Observable<LagDTO[]> {
    return this.http.get<LagDTO[]>(`${environment.khcUrl}/monthly-report/${reportId}/days`);
  }

  updateLags(lags: LagDTO[]) {
    return this.http.put<IdDTO>(`${environment.khcUrl}/lags`, lags);
  }

  developments(objectId: string | undefined): Observable<any[]> {
    return this.http.get<DevelopmentDTO[]>(`${environment.khcUrl}/objects/${objectId}/developments`);
  }

  checkDevelopments(objectId: string | undefined) {
    return this.http.get<DevelopmentDTO[]>(`${environment.khcUrl}/objects/${objectId}/check-developments`);
  }

  saveDevelopment(development: DevelopmentDTO) {
    return this.http.put<IdDTO>(`${environment.khcUrl}/developments/${development.id}`, development);
  }

  developmentsBefore(objectId: string, month: string): Observable<DevelopmentDTO[]> {
    return this.http.get<DevelopmentDTO[]>(`${environment.khcUrl}/objects/${objectId}/developments-before?month=${month}`);
  }

  developmentCurrent(objectId: string, month: string): Observable<DevelopmentDTO> {
    return this.http.get<DevelopmentDTO>(`${environment.khcUrl}/objects/${objectId}/current-development?month=${month}`);
  }

  shareholders(objectId: string): Observable<ShareholderDTO[]> {
    return this.http.get<ShareholderDTO[]>(`${environment.khcUrl}/objects/${objectId}/shareholders`);
  }

  addShareholder(objectId?: string, dto?: AddShareholderDTO) : Observable<ShareholderDTO> {
    return this.http.post<ShareholderDTO>(`${environment.khcUrl}/objects/${objectId}/shareholders`, dto);
  }

  getTable7Data(reportId?: string) : Observable<Table7DataDTO> {
    return this.http.get<Table7DataDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/table7`);
  }

  getTable7AdditionalDataByMonthlyReport(reportId?: string) : Observable<Table7AdditionalDataDTO> {
    return this.http.get<Table7AdditionalDataDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/table7-additional`);
  }

  getTable7AdditionalDataByObjectIdAndMonth(objectId: string, month: string): Observable<Table7AdditionalDataDTO> {
    return this.http.get<Table7AdditionalDataDTO>(`${environment.khcUrl}/objects/${objectId}/table7-additional?month=${month}`);
  }

  getTable7AdditionalDataByObjectId(objectId?: string) : Observable<Table7AdditionalDataDTO> {
    return this.http.get<Table7AdditionalDataDTO>(`${environment.khcUrl}/objects/${objectId}/table7-additional`);
  }

  updateReportInitial(reportId: string, value: number): Observable<IdDTO> {
    const data = {value : value};
    return this.http.put<IdDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/payment-initial`, data);
  }

  updateReportFinal(reportId: string, value: number): Observable<IdDTO> {
    const data = {value : value};
    return this.http.put<IdDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/payment-initial`, data);
  }

  getPaymentData(reportId?: string) : Observable<PaymentDataDTO> {
    return this.http.get<PaymentDataDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/payment-data`);
  }

  //
  // TABLE 8
  //

  getTable8Data(objectId: string): Observable<Table8DataDTO> {
    return this.http.get<Table8DataDTO>(`${environment.khcUrl}/objects/${objectId}/table8`);
  }

  updateTable8BuildContractPrice(objectId: string, value: number): Observable<Table8DataDTO> {
    const data = {value : value};
    return this.http.put<Table8DataDTO>(`${environment.khcUrl}/objects/${objectId}/table8/build-contract-price`, data);
  }

  updateTable8BuildDesignPrice(objectId: string, value: number): Observable<Table8DataDTO> {
    const data = {value : value};
    return this.http.put<Table8DataDTO>(`${environment.khcUrl}/objects/${objectId}/table8/build-design-price`, data);
  }

  updateTable8MaterialContractPrice(objectId: string, value: number): Observable<Table8DataDTO> {
    const data = {value : value};
    return this.http.put<Table8DataDTO>(`${environment.khcUrl}/objects/${objectId}/table8/material-contract-price`, data);
  }

  updateTable8MaterialDesignPrice(objectId: string, value: number): Observable<Table8DataDTO> {
    const data = {value : value};
    return this.http.put<Table8DataDTO>(`${environment.khcUrl}/objects/${objectId}/table8/material-design-price`, data);
  }

  updateTable8DesignSupervisionContractPrice(objectId: string, value: number): Observable<Table8DataDTO> {
    const data = {value : value};
    return this.http.put<Table8DataDTO>(`${environment.khcUrl}/objects/${objectId}/table8/design-supervision-contract-price`, data);
  }

  updateTable8DesignSupervisionDesignPrice(objectId: string, value: number): Observable<Table8DataDTO> {
    const data = {value : value};
    return this.http.put<Table8DataDTO>(`${environment.khcUrl}/objects/${objectId}/table8/design-supervision-design-price`, data);
  }

  updateTable8DesignSupervisionInvestmentContractPrice(objectId: string, value: number): Observable<Table8DataDTO> {
    const data = {value : value};
    return this.http.put<Table8DataDTO>(`${environment.khcUrl}/objects/${objectId}/table8/design-supervision-investment-contract-price`, data);
  }

  updateTable8DesignSupervisionInvestmentDesignPrice(objectId: string, value: number): Observable<Table8DataDTO> {
    const data = {value : value};
    return this.http.put<Table8DataDTO>(`${environment.khcUrl}/objects/${objectId}/table8/design-supervision-investment-design-price`, data);
  }

  updateTable8TechnicalSupervisionContractPrice(objectId: string, value: number): Observable<Table8DataDTO> {
    const data = {value : value};
    return this.http.put<Table8DataDTO>(`${environment.khcUrl}/objects/${objectId}/table8/technical-supervision-contract-price`, data);
  }

  updateTable8TechnicalSupervisionDesignPrice(objectId: string, value: number): Observable<Table8DataDTO> {
    const data = {value : value};
    return this.http.put<Table8DataDTO>(`${environment.khcUrl}/objects/${objectId}/table8/technical-supervision-design-price`, data);
  }

  updateTable8InvestmentContractPrice(objectId: string, value: number): Observable<Table8DataDTO> {
    const data = {value : value};
    return this.http.put<Table8DataDTO>(`${environment.khcUrl}/objects/${objectId}/table8/investment-contract-price`, data);
  }

  updateTable8InvestmentDesignPrice(objectId: string, value: number): Observable<Table8DataDTO> {
    const data = {value : value};
    return this.http.put<Table8DataDTO>(`${environment.khcUrl}/objects/${objectId}/table8/investment-design-price`, data);
  }

  updateTable8IndependentAuditContractPrice(objectId: string, value: number): Observable<Table8DataDTO> {
    const data = {value : value};
    return this.http.put<Table8DataDTO>(`${environment.khcUrl}/objects/${objectId}/table8/independent-audit-contract-price`, data);
  }

  updateTable8IndependentAuditDesignPrice(objectId: string, value: number): Observable<Table8DataDTO> {
    const data = {value : value};
    return this.http.put<Table8DataDTO>(`${environment.khcUrl}/objects/${objectId}/table8/independent-audit-design-price`, data);
  }


  updateTable8IndependentDesignAuditContractPrice(objectId: string, value: number): Observable<Table8DataDTO> {
    const data = {value : value};
    return this.http.put<Table8DataDTO>(`${environment.khcUrl}/objects/${objectId}/table8/independent-design-audit-contract-price`, data);
  }

  updateTable8IndependentDesignAuditDesignPrice(objectId: string, value: number): Observable<Table8DataDTO> {
    const data = {value : value};
    return this.http.put<Table8DataDTO>(`${environment.khcUrl}/objects/${objectId}/table8/independent-design-audit-design-price`, data);
  }

  // 
  // VERTICAL APPLICATION
  // 

  getVerticalApplicationData(reportId: string): Observable<VerticalApplicationDTO> {
    return this.http.get<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application`);
  }

  updateVerticalApplicationParkingFact(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/parking-fact`, data);
  }

  updateVerticalApplicationLandscapeFact(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/landscape-fact`, data);
  }

  updateVerticalApplicationBasementFact(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/basement-fact`, data);
  }

  updateVerticalApplicationExcavationFact(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/excavation-fact`, data);
  }

  updateVerticalApplicationElevatorFact(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/elevator-fact`, data);
  }

  updateVerticalApplicationCommercialFact(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/commercial-fact`, data);
  }

  updateVerticalApplicationRoofingFact(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/roofing`, data);
  }

  updateVerticalApplicationReinforcementConcreteFact(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/reinforcement-concrete-fact`, data);
  }

  updateVerticalApplicationInternalDecorationFact(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/internal-decoration-fact`, data);
  }

  updateVerticalApplicationExteriorFinishFact(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/exterior-finish-fact`, data);
  }

  updateVerticalApplicationFlooringFact(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/flooring-fact`, data);
  }

  updateVerticalApplicationOpeningsFact(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/openings-fact`, data);
  }

  updateVerticalApplicationElectricityFact(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/electricity-fact`, data);
  }

  updateVerticalApplicationLowCurrentFact(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/low-current-fact`, data);
  }

  updateVerticalApplicationHeatingVentilationFact(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/heating-ventilation-fact`, data);
  }

  updateVerticalApplicationPlumberSewageFact(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/plumber-sewage-fact`, data);
  }

  updateVerticalApplicationWallFact(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/wall-fact`, data);
  }

  updateVerticalApplicationParkingPlan(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/parking-plan`, data);
  }

  updateVerticalApplicationLandscapePlan(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/landscape-plan`, data);
  }

  updateVerticalApplicationBasementPlan(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/basement-plan`, data);
  }

  updateVerticalApplicationExcavationPlan(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/excavation-plan`, data);
  }

  updateVerticalApplicationElevatorPlan(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/elevator-plan`, data);
  }

  updateVerticalApplicationCommercialPlan(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/commercial-plan`, data);
  }

  updateVerticalApplicationRoofingPlan(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/roofing-plan`, data);
  }

  updateVerticalApplicationReinforcementConcretePlan(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/reinforcement-concrete-plan`, data);
  }

  updateVerticalApplicationInternalDecorationPlan(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/internal-decoration-plan`, data);
  }

  updateVerticalApplicationExteriorFinishPlan(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/exterior-finish-plan`, data);
  }

  updateVerticalApplicationFlooringPlan(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/flooring-plan`, data);
  }

  updateVerticalApplicationOpeningsPlan(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/openings-plan`, data);
  }

  updateVerticalApplicationElectricityPlan(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/electricity-plan`, data);
  }

  updateVerticalApplicationLowCurrentPlan(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/low-current-plan`, data);
  }

  updateVerticalApplicationHeatingVentilationPlan(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/heating-ventilation-plan`, data);
  }

  updateVerticalApplicationPlumberSewagePlan(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/plumber-sewage-plan`, data);
  }

  updateVerticalApplicationWallPlan(reportId: string, value: number): Observable<VerticalApplicationDTO> {
    const data = {value : value};
    return this.http.put<VerticalApplicationDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/vertical-application/wall-plan`, data);
  }

  financingPlans(objectId: string): Observable<FinancingPlanDTO[]> {
    return this.http.get<FinancingPlanDTO[]>(`${environment.khcUrl}/objects/${objectId}/financing-plans`);
  }

  updateFinancingPlan(financingPlanId: string, plan: number): Observable<FinancingPlanDTO> {
    const data = {value : plan};
    return this.http.put<FinancingPlanDTO>(`${environment.khcUrl}/financing-plans/${financingPlanId}`, data);
  }

  table9Data(reportId: string): Observable<Table9DataDTO> {
    return this.http.get<Table9DataDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/table9`);
  }


  incomes(objectId: string): Observable<IncomeDTO[]> {
    return this.http.get<IncomeDTO[]>(`${environment.khcUrl}/objects/${objectId}/incomes`);
  }

  updateBank(incomeId: string, income: number): Observable<IncomeDTO> {
    const data = {value : income};
    return this.http.put<IncomeDTO>(`${environment.khcUrl}/incomes/${incomeId}/bank`, data);
  }

  updateInvestor(incomeId: string, income: number): Observable<IncomeDTO> {
    const data = {value : income};
    return this.http.put<IncomeDTO>(`${environment.khcUrl}/incomes/${incomeId}/investor`, data);
  }

  updateBooking(incomeId: string, income: number): Observable<IncomeDTO> {
    const data = {value : income};
    return this.http.put<IncomeDTO>(`${environment.khcUrl}/incomes/${incomeId}/booking`, data);
  }

  updateShareholders(incomeId: string, income: number): Observable<IncomeDTO> {
    const data = {value : income};
    return this.http.put<IncomeDTO>(`${environment.khcUrl}/incomes/${incomeId}/shareholders`, data);
  }

  updateOtherShareholders(incomeId: string, income: number): Observable<IncomeDTO> {
    const data = {value : income};
    return this.http.put<IncomeDTO>(`${environment.khcUrl}/incomes/${incomeId}/other-shareholders`, data);
  }

  updateOther3Percent(incomeId: string, income: number): Observable<IncomeDTO> {
    const data = {value : income};
    return this.http.put<IncomeDTO>(`${environment.khcUrl}/incomes/${incomeId}/other-3percent`, data);
  }

  payment(paymentId: string): Observable<PaymentDTO> {
    return this.http.get<PaymentDTO>(`${environment.khcUrl}/payments/${paymentId}`);
  }

  shareholdersByIin(objectId: string, iin?: string) {
    return this.http.get<ShareholderDTO[]>(`${environment.khcUrl}/objects/${objectId}/shareholders?iin=${iin}`);
  }

  assignShareholderToPayment(shareholderId: string, paymentId: string, amount: number) : Observable<ShareholderPaymentDTO[]> {
    const data = {shareholderId : shareholderId, amount: amount};
    return this.http.put<ShareholderPaymentDTO[]>(`${environment.khcUrl}/payments/${paymentId}/shareholders`, data);
  }

  paymentShareholders(paymentId: string) {
    return this.http.get<ShareholderPaymentDTO[]>(`${environment.khcUrl}/payments/${paymentId}/shareholders`);
  }

  shareholder(shareholderId: string) {
    return this.http.get<ShareholderDTO>(`${environment.khcUrl}/shareholders/${shareholderId}`);
  }

  shareholderPayments(shareholderId: string) {
    return this.http.get<ShareholderPaymentDTO[]>(`${environment.khcUrl}/shareholders/${shareholderId}/payments`);
  }

  table6BeforeData(objectId: string): Observable<Table6BeforeDataDTO> {
    return this.http.get<Table6BeforeDataDTO>(`${environment.khcUrl}/objects/${objectId}/table6-before`);
  }

  updateTable6BudgetPlannedDesign(objectId: string, value: number): Observable<Table6BeforeDataDTO> {
    const data = {value : value};
    return this.http.put<Table6BeforeDataDTO>(`${environment.khcUrl}/objects/${objectId}/table6-before-budget-planned-design`, data);
  }

  updateTable6PaymentBeforeDesign(objectId: string, value: number): Observable<Table6BeforeDataDTO> {
    const data = {value : value};
    return this.http.put<Table6BeforeDataDTO>(`${environment.khcUrl}/objects/${objectId}/table6-before-payment-before-design`, data);
  }

  updateTable6UsedBeforeDesign(objectId: string, value: number): Observable<Table6BeforeDataDTO> {
    const data = {value : value};
    return this.http.put<Table6BeforeDataDTO>(`${environment.khcUrl}/objects/${objectId}/table6-before-used-before-design`, data);
  }

  updateTable6BudgetPlannedExpertise(objectId: string, value: number): Observable<Table6BeforeDataDTO> {
    const data = {value : value};
    return this.http.put<Table6BeforeDataDTO>(`${environment.khcUrl}/objects/${objectId}/table6-before-budget-planned-expertise`, data);
  }

  updateTable6PaymentBeforeExpertise(objectId: string, value: number): Observable<Table6BeforeDataDTO> {
    const data = {value : value};
    return this.http.put<Table6BeforeDataDTO>(`${environment.khcUrl}/objects/${objectId}/table6-before-payment-before-expertise`, data);
  }

  updateTable6UsedBeforeExpertise(objectId: string, value: number): Observable<Table6BeforeDataDTO> {
    const data = {value : value};
    return this.http.put<Table6BeforeDataDTO>(`${environment.khcUrl}/objects/${objectId}/table6-before-used-before-expertise`, data);
  }

  updateTable6BudgetPlannedConstruction(objectId: string, value: number): Observable<Table6BeforeDataDTO> {
    const data = {value : value};
    return this.http.put<Table6BeforeDataDTO>(`${environment.khcUrl}/objects/${objectId}/table6-before-budget-planned-construction`, data);
  }

  updateTable6PaymentBeforeConstruction(objectId: string, value: number): Observable<Table6BeforeDataDTO> {
    const data = {value : value};
    return this.http.put<Table6BeforeDataDTO>(`${environment.khcUrl}/objects/${objectId}/table6-before-payment-before-construction`, data);
  }

  updateTable6UsedBeforeConstruction(objectId: string, value: number): Observable<Table6BeforeDataDTO> {
    const data = {value : value};
    return this.http.put<Table6BeforeDataDTO>(`${environment.khcUrl}/objects/${objectId}/table6-before-used-before-construction`, data);
  }

  updateTable6BudgetPlannedConstructionPrepaid(objectId: string, value: number): Observable<Table6BeforeDataDTO> {
    const data = {value : value};
    return this.http.put<Table6BeforeDataDTO>(`${environment.khcUrl}/objects/${objectId}/table6-before-budget-planned-construction-prepaid`, data);
  }

  updateTable6PaymentBeforeConstructionPrepaid(objectId: string, value: number): Observable<Table6BeforeDataDTO> {
    const data = {value : value};
    return this.http.put<Table6BeforeDataDTO>(`${environment.khcUrl}/objects/${objectId}/table6-before-payment-before-construction-prepaid`, data);
  }

  updateTable6UsedBeforeConstructionPrepaid(objectId: string, value: number): Observable<Table6BeforeDataDTO> {
    const data = {value : value};
    return this.http.put<Table6BeforeDataDTO>(`${environment.khcUrl}/objects/${objectId}/table6-before-used-before-construction-prepaid`, data);
  }

  updateTable6BudgetPlannedDesignSupervision(objectId: string, value: number): Observable<Table6BeforeDataDTO> {
    const data = {value : value};
    return this.http.put<Table6BeforeDataDTO>(`${environment.khcUrl}/objects/${objectId}/table6-before-budget-planned-design-supervision`, data);
  }

  updateTable6PaymentBeforeDesignSupervision(objectId: string, value: number): Observable<Table6BeforeDataDTO> {
    const data = {value : value};
    return this.http.put<Table6BeforeDataDTO>(`${environment.khcUrl}/objects/${objectId}/table6-before-payment-before-design-supervision`, data);
  }

  updateTable6UsedBeforeDesignSupervision(objectId: string, value: number): Observable<Table6BeforeDataDTO> {
    const data = {value : value};
    return this.http.put<Table6BeforeDataDTO>(`${environment.khcUrl}/objects/${objectId}/table6-before-used-before-design-supervision`, data);
  }

  updateTable6BudgetPlannedTechnicalSupervision(objectId: string, value: number): Observable<Table6BeforeDataDTO> {
    const data = {value : value};
    return this.http.put<Table6BeforeDataDTO>(`${environment.khcUrl}/objects/${objectId}/table6-before-budget-planned-technical-supervision`, data);
  }

  updateTable6PaymentBeforeTechnicalSupervision(objectId: string, value: number): Observable<Table6BeforeDataDTO> {
    const data = {value : value};
    return this.http.put<Table6BeforeDataDTO>(`${environment.khcUrl}/objects/${objectId}/table6-before-payment-before-technical-supervision`, data);
  }

  updateTable6UsedBeforeTechnicalSupervision(objectId: string, value: number): Observable<Table6BeforeDataDTO> {
    const data = {value : value};
    return this.http.put<Table6BeforeDataDTO>(`${environment.khcUrl}/objects/${objectId}/table6-before-used-before-technical-supervision`, data);
  }

  updateTable6BudgetPlannedOther(objectId: string, value: number): Observable<Table6BeforeDataDTO> {
    const data = {value : value};
    return this.http.put<Table6BeforeDataDTO>(`${environment.khcUrl}/objects/${objectId}/table6-before-budget-planned-other`, data);
  }

  updateTable6PaymentBeforeOther(objectId: string, value: number): Observable<Table6BeforeDataDTO> {
    const data = {value : value};
    return this.http.put<Table6BeforeDataDTO>(`${environment.khcUrl}/objects/${objectId}/table6-before-payment-before-other`, data);
  }

  updateTable6UsedBeforeOther(objectId: string, value: number): Observable<Table6BeforeDataDTO> {
    const data = {value : value};
    return this.http.put<Table6BeforeDataDTO>(`${environment.khcUrl}/objects/${objectId}/table6-before-used-before-other`, data);
  }

  getTable6Data(reportId: string) : Observable<Table6DataDTO> {
    return this.http.get<Table6DataDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/table6`);
  }

  updateCompletedMonthDesign(reportId: string, value: number) {
    const data = {value : value};
    return this.http.put<Table6BeforeDataDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/table6-completed-month-design`, data);
  }

  updateCompletedMonthExpertise(reportId: string, value: number) {
    const data = {value : value};
    return this.http.put<Table6BeforeDataDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/table6-completed-month-expertise`, data);
  }

  updateCompletedMonthConstructionPrepaid(reportId: string, value: number) {
    const data = {value : value};
    return this.http.put<Table6BeforeDataDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/table6-completed-month-construction-prepaid`, data);
  }

  updateCompletedMonthDesignSupervision(reportId: string, value: number) {
    const data = {value : value};
    return this.http.put<Table6BeforeDataDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/table6-completed-month-design-supervision`, data);
  }

  updateCompletedMonthTechnicalSupervision(reportId: string, value: number) {
    const data = {value : value};
    return this.http.put<Table6BeforeDataDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/table6-completed-month-technical-supervision`, data);
  }

  updateCompletedMonthOther(reportId: string, value: number) {
    const data = {value : value};
    return this.http.put<Table6BeforeDataDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/table6-completed-month-other`, data);
  }

  updateConstructiveFact(table3: Table3DataDTO, row: Table3Data, value: number) {
    const data = {value : value};
    return this.http.put<PlanDTO>(`${environment.khcUrl}/constructives/${row.id}/${table3.month}/fact`, data);
  }

  searchShareholders(objectId: string, search?: string): Observable<ShareholderDTO[]> {
    return this.http.get<ShareholderDTO[]>(`${environment.khcUrl}/objects/${objectId}/shareholders?search=${search}`);
  }

  updateShareholderIin(shareholderId: string, text: string) {
    const data = {value : text};
    return this.http.put<ShareholderDTO>(`${environment.khcUrl}/shareholders/${shareholderId}/iin`, data);
  }

  updateShareholderLastName(shareholderId: string, text: string) {
    const data = {value : text};
    return this.http.put<ShareholderDTO>(`${environment.khcUrl}/shareholders/${shareholderId}/last-name`, data);
  }

  updateShareholderFirstName(shareholderId: string, text: string) {
    const data = {value : text};
    return this.http.put<ShareholderDTO>(`${environment.khcUrl}/shareholders/${shareholderId}/first-name`, data);
  }

  updateShareholderFatherName(shareholderId: string, text: string) {
    const data = {value : text};
    return this.http.put<ShareholderDTO>(`${environment.khcUrl}/shareholders/${shareholderId}/father-name`, data);
  }

  updateShareholderContractNumber(shareholderId: string, text: string) {
    const data = {value : text};
    return this.http.put<ShareholderDTO>(`${environment.khcUrl}/shareholders/${shareholderId}/contract-number`, data);
  }

  updateShareholderContractDate(shareholderId: string, date: Date) {
    const data = {value : date};
    return this.http.put<ShareholderDTO>(`${environment.khcUrl}/shareholders/${shareholderId}/contract-date`, data);
  }

  updateShareholderTerminationDate(shareholderId: string, date: Date) {
    const data = {value : date};
    return this.http.put<ShareholderDTO>(`${environment.khcUrl}/shareholders/${shareholderId}/termination-date`, data);
  }

  updateShareholderFloor(shareholderId: string, text: string) {
    const data = {value : text};
    return this.http.put<ShareholderDTO>(`${environment.khcUrl}/shareholders/${shareholderId}/floor`, data);
  }

  updateShareholderNumber(shareholderId: string, text: string) {
    const data = {value : text};
    return this.http.put<ShareholderDTO>(`${environment.khcUrl}/shareholders/${shareholderId}/number`, data);
  }

  updateShareholderTotalArea(shareholderId: string, value: number) {
    const data = {value : value};
    return this.http.put<ShareholderDTO>(`${environment.khcUrl}/shareholders/${shareholderId}/total-area`, data);
  }

  updateShareholderTotalPrice(shareholderId: string, value: number) {
    const data = {value : value};
    return this.http.put<ShareholderDTO>(`${environment.khcUrl}/shareholders/${shareholderId}/total-price`, data);
  }

  updateShareholderInitialAmount(shareholderId: string, value: number) {
    const data = {value : value};
    return this.http.put<ShareholderDTO>(`${environment.khcUrl}/shareholders/${shareholderId}/initial-amount`, data);
  }

  updateShareholderStatus(shareholderId: string, value: string) {
    const data = {value : value};
    return this.http.put<ShareholderDTO>(`${environment.khcUrl}/shareholders/${shareholderId}/status`, data);
  }

  updateShareholderShareName(shareholderId: string, value: string) {
    const data = {value : value};
    return this.http.put<ShareholderDTO>(`${environment.khcUrl}/shareholders/${shareholderId}/share-name`, data);
  }

  updateShareholderSellDetails(shareholderId: string, value: string) {
    const data = {value : value};
    return this.http.put<ShareholderDTO>(`${environment.khcUrl}/shareholders/${shareholderId}/sell-details`, data);
  }

  updateEnpf(shareholderId: string, value: string) {
    const data = {value : value};
    return this.http.put<ShareholderDTO>(`${environment.khcUrl}/shareholders/${shareholderId}/enpf`, data);
  }

  deleteShareholder(shareholderId: string) {
    return this.http.delete(`${environment.khcUrl}/shareholders/${shareholderId}`);
  }

  table3AdditionalData(reportId: string) : Observable<Table3AdditionalDataDTO> {
    return this.http.get<Table3AdditionalDataDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/table3-additional`);
  }

  addComment(dto: AddCommentDTO) : Observable<CommentDTO> {
    return this.http.post<CommentDTO>(`${environment.khcUrl}/comments`, dto);
  }

  comments(entityId: string, type: string) : Observable<CommentDTO[]> {
    return this.http.get<CommentDTO[]>(`${environment.khcUrl}/comments/${entityId}/${type}`);
  }

  paymentsForReport(reportId: string) : Observable<PaymentDTO[]> {
    return this.http.get<PaymentDTO[]>(`${environment.khcUrl}/monthly-reports/${reportId}/payments`);
  }

  allComments(reportId: string) : Observable<CommentDTO[]> {
    return this.http.get<CommentDTO[]>(`${environment.khcUrl}/monthly-reports/${reportId}/comments`);
  }

  closeComment(commentId: string) : Observable<CommentDTO> {
    return this.http.put<CommentDTO>(`${environment.khcUrl}/comments/${commentId}/close`, {});
  }

  openComment(commentId: string) : Observable<CommentDTO>  {
    return this.http.put<CommentDTO>(`${environment.khcUrl}/comments/${commentId}/open`, {});
  }

  shareholdersByReportId(reportId: string): Observable<ShareholderDTO[]> {
    return this.http.get<ShareholderDTO[]>(`${environment.khcUrl}/shareholders-by-report/${reportId}`);
  }

  deleteFile(fileId : string) : Observable<IdDTO> {
    return this.http.delete<IdDTO>(`${environment.khcUrl}/files/${fileId}`);
  }

  table3Data(reportId: string): Observable<Table3DataDTO> {
    return this.http.get<Table3DataDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/table3`);
  }

  object(objectId: string): Observable<ObjectDTO> {
    return this.http.get<ObjectDTO>(`${environment.khcUrl}/objects/${objectId}`);
  }

  getPrescriptions(objectId: string): Observable<PrescriptionDTO[]> {
    return this.http.get<PrescriptionDTO[]>(`${environment.khcUrl}/objects/${objectId}/prescriptions`);
  }

  updatePrescriptionById(prescriptionId: string, data: UpdatePrescriptionDataDTO) : Observable<PrescriptionDTO> {
    return this.http.put<PrescriptionDTO>(`${environment.khcUrl}/prescriptions/${prescriptionId}/data`, data);
  }

  deleteShareholderPayment(shareholderPaymentId: string) {
    return this.http.delete(`${environment.khcUrl}/shareholders/${shareholderPaymentId}/payments`);
  }

  addShareholderPayment(shareholderPaymentId: string, value: Partial<{ amount: number; date: Date; }>) {
    return this.http.post(`${environment.khcUrl}/shareholders/${shareholderPaymentId}/payments`, value);
  }

  setTable7Shareholders(reportId: string, value: number) {
    const data = {value: value};
    return this.http.put<IdDTO>(`${environment.khcUrl}/table7/${reportId}/shareholders`, data);
  }

  setTable7OtherShareholders(reportId: string, value: number) {
    const data = {value: value};
    return this.http.put<IdDTO>(`${environment.khcUrl}/table7/${reportId}/other-shareholders`, data);
  }

  setTable7Bookings(reportId: string, value: number) {
    const data = {value: value};
    return this.http.put<IdDTO>(`${environment.khcUrl}/table7/${reportId}/bookings`, data);
  }

  setTable7Other3Percent(reportId: string, value: number) {
    const data = {value: value};
    return this.http.put<IdDTO>(`${environment.khcUrl}/table7/${reportId}/other-3-percent`, data);
  }

  setTable7Investor(reportId: string, value: number) {
    const data = {value: value};
    return this.http.put<IdDTO>(`${environment.khcUrl}/table7/${reportId}/investor`, data);
  }

  setTable7Bank(reportId: string, value: number) {
    const data = {value: value};
    return this.http.put<IdDTO>(`${environment.khcUrl}/table7/${reportId}/bank`, data);
  }

  searchCuratorByEmail(curatorEmail?: string): Observable<CuratorDTO> {
    return this.http.get<CuratorDTO>(`${environment.apiUrl}/api/khc/curator?email=${curatorEmail}`);
  }

  setCurator(objectId: string, curator?: CuratorDTO) {
    return this.http.put(`${environment.khcUrl}/objects/${objectId}/curator`, curator);
  }

  createProlongation(objectId: string, dto: NewProlongationDTO) : Observable<IdDTO> {
    return this.http.post<IdDTO>(`${environment.khcUrl}/objects/${objectId}/prolongations`, dto);
  }

  prolongations(objectId: string): Observable<ProlongationDTO[]> {
    return this.http.get<ProlongationDTO[]>(`${environment.khcUrl}/objects/${objectId}/prolongations`);
  }

  deleteProlongation(prolongationId: string) {
    return this.http.delete<IdDTO>(`${environment.khcUrl}/prolongations/${prolongationId}`);
  }

  deletePayments(reportId: string) {
    return this.http.delete<IdDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/payments`);
  }

  deletePhotoByFileId(fileId: string) {
    return this.http.delete<IdDTO>(`${environment.khcUrl}/monthly-report-photo?fileId=${fileId}`);
  }

  updateTable6BudgetPlannedUnexpectedExpenses(objectId: string, value: number) : Observable<IdDTO> {
    const data = {value : value};
    return this.http.put<IdDTO>(`${environment.khcUrl}/objects/${objectId}/table6-budget-planned-unexpected-expenses`, data);
  }

  updateTable6PaymentBeforeUnexpectedExpenses(objectId: string, value: number) : Observable<IdDTO> {
    const data = {value : value};
    return this.http.put<IdDTO>(`${environment.khcUrl}/objects/${objectId}/table6-payment-before-unexpected-expenses`, data);
  }

  updateTable6UsedBeforeUnexpectedExpenses(objectId: string, value: number) : Observable<IdDTO> {
    const data = {value : value};
    return this.http.put<IdDTO>(`${environment.khcUrl}/objects/${objectId}/table6-used-before-unexpected-expenses`, data);
  }

  monthlyReportProcessesById(reportId: string): Observable<any> {
    // return this.http.get<any>(`${environment.apiUrl}/api/process-api/history/historic-process-instances?businessKey=${reportId}&processDefinitionKey=KHCMonthlyReportProcess&sort=startTime&order=desc&start=0&size=20`);
    return this.http.get<any>(`${environment.apiUrl}/api/process-api/runtime/process-instances?businessKey=${reportId}&processDefinitionKey=KHCMonthlyReportProcess&sort=startTime&order=desc&start=0&size=20&suspended=false`);
  }

  setTable6PaymentConstruction(reportId: string, value: number) {
    const data = {value: value};
    return this.http.put<IdDTO>(`${environment.khcUrl}/table6/${reportId}/construction`, data);
  }

  setTable6PaymentConstructionPrepaid(reportId: string, value: number) {
    const data = {value: value};
    return this.http.put<IdDTO>(`${environment.khcUrl}/table6/${reportId}/construction-prepaid`, data);
  }

  setTable6PaymentTechnicalSupervision(reportId: string, value: number) {
    const data = {value: value};
    return this.http.put<IdDTO>(`${environment.khcUrl}/table6/${reportId}/technical-supervision`, data);
  }

  setTable6PaymentDesignSupervision(reportId: string, value: number) {
    const data = {value: value};
    return this.http.put<IdDTO>(`${environment.khcUrl}/table6/${reportId}/design-supervision`, data);
  }

  setTable6PaymentDesign(reportId: string, value: number) {
    const data = {value: value};
    return this.http.put<IdDTO>(`${environment.khcUrl}/table6/${reportId}/design`, data);
  }

  setTable6PaymentOther(reportId: string, value: number) {
    const data = {value: value};
    return this.http.put<IdDTO>(`${environment.khcUrl}/table6/${reportId}/other`, data);
  }

  addPrescriptionsRow(objectId: string, name: string) : Observable<IdDTO> {
    return this.http.post<IdDTO>(`${environment.khcUrl}/objects/${objectId}/prescriptions`, { name: name});
  }

  getPrescriptionCategories(): Observable<PrescriptionCategoryDTO[]> {
    return this.http.get<PrescriptionCategoryDTO[]>(`${environment.khcUrl}/prescription-categories`);
  }

  addMovement(reportId?: string, movement?: { iin?: string; lastName?: string; firstName?: string; fatherName?: string; email?: string; reportId?: string; status?: string; comments?: string; }) : Observable<IdDTO> {
    return this.http.post<IdDTO>(`${environment.khcUrl}/monthly-reports/${reportId}/movements`, movement);
  }
}
